import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { Schema } from '@/widgets/FormBuilder/types';
import { SearchFormTemplate } from '@/widgets/Templates';

import ErzSearchSchema from '@/shared/data/schemas/services/ERZSEARCH.json';
import { IntegrationSystem } from '@/shared/types';
import { GridProps, InformerProps } from '@/shared/ui';

import { useMutationFormSubmit } from '@/hooks/useMutationFormSubmit';

import { ServiceNames } from '@/config/services';

import { formatDate } from '@/helpers/dates';

import { ERZExtractKind, ErzSearchQueryParams, ExtractDetails } from '@/api/types';

import api from '@/api';

const getInformer = (erzResult?: ExtractDetails<IntegrationSystem.ERZ>): InformerProps | undefined => {
  if (!erzResult) {
    return undefined;
  }
  if (erzResult?.kind === ERZExtractKind.ALL_EXISTING_PERMITS) {
    return {
      text: {
        text: 'Перевірка витягів цього типу наразі в активній розробці та скоро зʼявиться на сайті',
        color: 'black',
      },
      header: { text: 'Увага', color: 'blue' },
      icon: { name: 'waiting', color: 'blue' },
      borderColor: 'blue',
      bgColor: 'sand',
    };
  }
  if (erzResult.info?.type === 'info') {
    return {
      text: { text: erzResult?.info?.message ?? '', color: 'black' },
      header: { text: 'Увага!', color: 'blue' },
      icon: { name: 'attention', color: 'blue' },
      borderColor: 'blue',
      bgColor: 'sand',
    };
  }
  if (erzResult.info?.type === 'danger') {
    return {
      text: { text: erzResult?.info?.message ?? '', color: 'black' },
      header: { text: 'Увага!', color: 'red' },
      icon: { name: 'waiting', color: 'red' },
      borderColor: 'dark-yellow',
      bgColor: 'sand',
    };
  }
};

export const ERZSearch: React.FC = () => {
  const { state } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(state?.search), [state]);

  const [initialValues] = useState<ErzSearchQueryParams>({
    numb: searchParams.get('number') || '',
    secret: searchParams.get('verify_code') || '',
  });

  const {
    formSubmit,
    result: erzResult,
    isLoading,
    hasBeenFetched,
  } = useMutationFormSubmit((params: ErzSearchQueryParams) => api.permits.searchExtract(params));
  const gridData =
    erzResult?.data?.length && erzResult.kind !== ERZExtractKind.ALL_EXISTING_PERMITS
      ? {
          title: erzResult?.data[0] && erzResult?.data[0].join(': '),
          data: erzResult.data
            .slice(1)
            .map<GridProps['data'][number]>(([title, data]: [string, string]) => [title, formatDate(data)]),
        }
      : { title: '', data: [] };

  return (
    <SearchFormTemplate
      serviceName={ServiceNames['erz.search']}
      formTitle="Пошук за номером витягу"
      formSchema={ErzSearchSchema as unknown as Schema}
      formSubmit={formSubmit}
      result={erzResult}
      isLoading={isLoading}
      hasBeenFetched={hasBeenFetched}
      initialValues={initialValues}
      validateOnMount={Boolean(initialValues.numb && initialValues.secret)}
      resultType={{
        type: 'grid',
        gridProps: {
          data: gridData.data,
          title: erzResult?.data[0] && erzResult?.data[0].join(': '),
          informer: getInformer(erzResult),
          placeholder: 'Не вказано',
        },
      }}
    />
  );
};
